/**
 * StaffAddPage — `/admin/staff/new` Add staff invite form (SCRUM-131).
 *
 * Single-screen form (not a wizard). Lives at `/admin/staff/new`. The
 * body component takes a `compact` prop that collapses Identity fields
 * to a single column for a future modal context.
 *
 * ## Layout (max-width 880 px, single form Card)
 *
 *   - **Section pill** — "Internal admin · Add staff" + H1
 *     "Invite an internal employee" + descriptive copy.
 *   - **Identity** — Work email only. A platform invite carries NO name:
 *     the backend `POST /admin/staff` body is `{ email, role }`, and the
 *     invitee supplies their first/last name at accept time (codex P2a).
 *     The work email shows a GENERIC non-blocking hint ("Use the
 *     employee's work email") — no vendor domain is named.
 *   - **Role** — exactly 2 radio cards (Root + Support), each a
 *     RoleOption with `<StaffRoleChip>` + a one-line description.
 *   - **Initial permissions** — read-only chip cloud derived from
 *     `PERMISSIONS_BY_ROLE[role]`. Updates live as the user picks a role.
 *   - **Action row** — Cancel (ghost) + Send invitation (primary).
 *
 * ## Validation (format-only)
 *
 *   - Work email: required + browser `type="email"` format. There is NO
 *     `@desata` hard-block (decision-6); the vendor domain is not chosen,
 *     so the hint is generic and non-blocking.
 *   - Role: required (no default).
 *
 * ## Submit
 *
 * Wires the real `useInviteStaff` mutation (`POST /admin/staff`):
 *   - success → in-page success surface (codex P2b), NOT a redirect.
 *     `GET /admin/staff` returns no pending invitations, so a redirect to
 *     the list would read as a failed invite and push retries into the
 *     duplicate path. The surface offers "Invite another" (resets the
 *     form) + "Back to staff".
 *   - 422-duplicate → inline non-blocking error (a pending invitation
 *     already exists);
 *   - 403 → not-permitted error.
 *
 * ## Permission gate
 *
 *   `staff:invite` — root-only (`PERMISSIONS_BY_ROLE`), matching the
 *   backend `PlatformRootGuard` on `POST /admin/staff`.
 */

import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Icon, TextField } from '@sparta/ui-kit';
import { IfAdminPermission } from '../../../shared/auth/IfAdminPermission.js';
import {
  PERMISSIONS_BY_ROLE,
  type AdminPermissionSlug,
  type PlatformRole,
} from '../../../shared/auth/admin-permissions.js';
import { StaffRoleChip } from '../_components/StaffRoleChip.js';
import { useCompactMode } from '../../../shared/hooks/useCompactMode.js';
import { useInviteStaff, type InviteStaffError } from '../api/index.js';

// ── Validation helpers ─────────────────────────────────────────

/**
 * Format-only email predicate. No vendor-domain gate (decision-6) — the
 * backend enforces uniqueness + any real policy. We only require a
 * minimally well-formed `local@domain.tld` shape so the form doesn't
 * fire the mutation on obviously-empty input.
 */
export function isWellFormedEmail(email: string): boolean {
  const trimmed = email.trim();
  if (trimmed.length === 0 || trimmed !== email) return false;
  if (trimmed.includes(' ')) return false;
  const atIdx = trimmed.indexOf('@');
  if (atIdx <= 0 || trimmed.lastIndexOf('@') !== atIdx) return false;
  const domain = trimmed.slice(atIdx + 1);
  return domain.includes('.') && !domain.startsWith('.') && !domain.endsWith('.');
}

// ── Form state model ──────────────────────────────────────────

interface StaffForm {
  readonly email: string;
  readonly role: PlatformRole | null;
}

const INITIAL_FORM: StaffForm = {
  email: '',
  role: null,
};

/**
 * Local role-label map (2 canonical roles). Drives the RoleOption
 * button's `aria-label` so the radio's accessible name is JUST the role
 * label, not label + description. The description rides on
 * `aria-describedby`.
 */
const ROLE_LABEL: Record<PlatformRole, string> = {
  root: 'Root',
  support: 'Support',
};

const ROLE_OPTIONS: ReadonlyArray<{
  readonly id: PlatformRole;
  readonly desc: string;
}> = [
  { id: 'root', desc: 'Full platform access incl. inviting + suspending staff. Use sparingly.' },
  { id: 'support', desc: 'Read access across the platform, plus password resets.' },
];

// ── Page entry ─────────────────────────────────────────────────

export interface StaffAddPageProps {
  readonly compact?: boolean;
}

export function StaffAddPage(
  props: StaffAddPageProps = {},
): React.ReactElement {
  return (
    <IfAdminPermission
      permission="staff:invite"
      fallback={<NotPermittedPanel />}
    >
      <StaffAddBody compact={props.compact} />
    </IfAdminPermission>
  );
}

function NotPermittedPanel(): React.ReactElement {
  return (
    <section
      aria-labelledby="staff-add-not-permitted-heading"
      className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-16 text-center"
    >
      <h1
        id="staff-add-not-permitted-heading"
        className="text-xl font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Not permitted
      </h1>
      <p className="mt-2 text-sm" style={{ color: 'var(--fg-secondary)' }}>
        Only platform administrators can invite staff. Ask a platform
        administrator if you need this access.
      </p>
      <div style={{ marginTop: '16px' }}>
        <Button asChild variant="secondary">
          <Link to="/admin/staff">Back to Staff</Link>
        </Button>
      </div>
    </section>
  );
}

// ── Invite-sent success surface (codex P2b) ────────────────────

function InviteSentPanel({
  email,
  compact,
  onInviteAnother,
}: {
  readonly email: string;
  readonly compact: boolean;
  readonly onInviteAnother: () => void;
}): React.ReactElement {
  return (
    <section
      aria-labelledby="staff-add-sent-heading"
      className={
        compact
          ? 'mx-auto w-full'
          : 'mx-auto w-full rounded-2xl border border-solid border-[var(--border-subtle)]'
      }
      style={{ maxWidth: '880px', padding: compact ? '16px' : '32px' }}
    >
      <div
        className="flex flex-col items-start"
        style={{ gap: '12px' }}
      >
        <span
          aria-hidden="true"
          className="inline-flex items-center justify-center rounded-full"
          style={{
            width: '44px',
            height: '44px',
            color: 'var(--success-500)',
            background: 'color-mix(in oklab, var(--success-500) 12%, var(--bg-surface))',
          }}
        >
          <Icon name="check" size={22} />
        </span>
        <h1
          id="staff-add-sent-heading"
          className="m-0 font-bold"
          style={{
            fontSize: compact ? '22px' : '26px',
            letterSpacing: '-0.4px',
            color: 'var(--fg-primary)',
          }}
        >
          Invitation sent
        </h1>
        {/* WCAG 4.1.3 Status Messages: announce the outcome without
            moving focus. The list never shows pending invites, so this
            in-page confirmation is the only success signal. */}
        <p
          role="status"
          aria-live="polite"
          className="m-0"
          style={{ fontSize: '14px', color: 'var(--fg-secondary)' }}
        >
          Invitation sent to{' '}
          <span className="font-mono" style={{ color: 'var(--fg-primary)' }}>
            {email}
          </span>
          . They'll receive a one-time link valid for 24 hours.
        </p>
      </div>
      <div
        className={
          compact
            ? 'mt-4 flex flex-col items-stretch'
            : 'mt-5 flex items-center'
        }
        style={{ gap: '12px', marginTop: '20px' }}
      >
        <Button
          type="button"
          variant="primary"
          full={compact}
          onClick={onInviteAnother}
        >
          <Icon name="mail" size={16} aria-hidden />
          Invite another
        </Button>
        <Button asChild variant="secondary" full={compact}>
          <Link to="/admin/staff">Back to staff</Link>
        </Button>
      </div>
    </section>
  );
}

// ── Body ───────────────────────────────────────────────────────

function StaffAddBody({
  compact: compactOverride,
}: StaffAddPageProps): React.ReactElement {
  const compact = useCompactMode(compactOverride);
  const navigate = useNavigate();
  const invite = useInviteStaff();
  const [form, setForm] = React.useState<StaffForm>(INITIAL_FORM);
  const [submitAttempted, setSubmitAttempted] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  // On a successful invite we replace the form with an in-page success
  // surface (codex P2b) — the list never shows pending invitations, so a
  // redirect would read as a failed invite. We retain the invited email
  // for the confirmation copy.
  const [invitedEmail, setInvitedEmail] = React.useState<string | null>(null);

  const patch = (next: Partial<StaffForm>): void => {
    setForm((prev) => ({ ...prev, ...next }));
    setSubmitError(null);
  };

  /**
   * Roving-focus map: lets the radiogroup keydown handler move keyboard
   * focus to the next/previous role button by its `PlatformRole` id. Per
   * WAI-ARIA radiogroup pattern exactly one role button is in the tab
   * order at a time; the others use `tabIndex={-1}`.
   */
  const roleButtonRefs = React.useRef<
    Map<PlatformRole, HTMLButtonElement | null>
  >(new Map());

  const handleRoleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ): void => {
    const currentIdx =
      form.role === null
        ? 0
        : ROLE_OPTIONS.findIndex((opt) => opt.id === form.role);
    let nextIdx = currentIdx;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      nextIdx = (currentIdx + 1) % ROLE_OPTIONS.length;
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      nextIdx =
        (currentIdx - 1 + ROLE_OPTIONS.length) % ROLE_OPTIONS.length;
    } else if (event.key === 'Home') {
      nextIdx = 0;
    } else if (event.key === 'End') {
      nextIdx = ROLE_OPTIONS.length - 1;
    } else {
      return;
    }
    event.preventDefault();
    const nextOption = ROLE_OPTIONS[nextIdx];
    if (nextOption === undefined) return;
    patch({ role: nextOption.id });
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        roleButtonRefs.current.get(nextOption.id)?.focus();
      });
    }
  };

  // Roving-focus tab stop: the selected role is the single tab-order
  // entry; if none selected, the first role takes the tab stop.
  const firstRole = ROLE_OPTIONS[0];
  if (firstRole === undefined) {
    throw new Error('ROLE_OPTIONS must contain at least one entry');
  }
  const focusedRole: PlatformRole = form.role ?? firstRole.id;

  // Validation gates — a platform invite is { email, role } only.
  const emailOk = isWellFormedEmail(form.email);
  const roleOk = form.role !== null;
  const canSubmit = emailOk && roleOk;

  const emailError =
    submitAttempted && !emailOk ? 'Enter a valid email address.' : undefined;
  const roleError =
    submitAttempted && !roleOk ? 'Please pick a role.' : undefined;

  const handleInviteAnother = (): void => {
    setInvitedEmail(null);
    setForm(INITIAL_FORM);
    setSubmitAttempted(false);
    setSubmitError(null);
  };

  const handleSubmit = (): void => {
    setSubmitAttempted(true);
    setSubmitError(null);
    if (!canSubmit || form.role === null) return;
    const email = form.email;
    void invite
      .mutateAsync({ email, role: form.role })
      .then(() => {
        setInvitedEmail(email);
      })
      .catch((err: InviteStaffError) => {
        if (err.kind === 'duplicate') {
          setSubmitError(
            'That email already has a pending invitation. They’ll receive the original link.',
          );
        } else if (err.kind === 'forbidden') {
          setSubmitError('You are not permitted to invite staff.');
        } else {
          setSubmitError('Could not send the invitation. Try again.');
        }
      });
  };

  const handleCancel = (): void => {
    navigate('/admin/staff');
  };

  if (invitedEmail !== null) {
    return (
      <InviteSentPanel
        email={invitedEmail}
        compact={compact}
        onInviteAnother={handleInviteAnother}
      />
    );
  }

  return (
    <section
      aria-labelledby="staff-add-heading"
      className={
        compact
          ? 'mx-auto w-full'
          : 'mx-auto w-full rounded-2xl border border-solid border-[var(--border-subtle)]'
      }
      style={{ maxWidth: '880px', padding: compact ? '16px' : '32px' }}
    >
      {/* Eyebrow badge */}
      <span
        className="inline-flex items-center font-semibold"
        style={{
          gap: '6px',
          padding: '4px 12px',
          borderRadius: 'var(--radius-full)',
          fontSize: '12px',
          color: 'var(--brand-700)',
          background: 'color-mix(in oklab, var(--brand-500) 12%, var(--bg-surface))',
          border: '1px solid color-mix(in oklab, var(--brand-500) 26%, transparent)',
          marginBottom: '12px',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: '6px',
            height: '6px',
            borderRadius: 'var(--radius-full)',
            background: 'var(--brand-500)',
          }}
        />
        Internal admin · Add staff
      </span>
      <h1
        id="staff-add-heading"
        className="m-0 font-bold"
        style={{
          fontSize: compact ? '22px' : '26px',
          letterSpacing: '-0.4px',
          color: 'var(--fg-primary)',
          marginBottom: '6px',
        }}
      >
        Invite an internal employee
      </h1>
      <p
        className="m-0"
        style={{
          fontSize: '14px',
          color: 'var(--fg-tertiary)',
          marginBottom: '24px',
        }}
      >
        Staff get platform-wide access bounded by their role. They will
        receive a one-time invitation email.
      </p>

      <Card padding="lg">
        {/* Identity */}
        <FieldGroup
          heading="Identity"
          headingId="staff-add-identity"
          description="Use the employee's work email — they'll sign in with it."
        >
          <TextField
            label="Work email"
            type="email"
            icon="mail"
            required
            autoComplete="off"
            placeholder="name@company.com"
            value={form.email}
            onChange={(e) => patch({ email: e.target.value })}
            error={emailError}
          />
        </FieldGroup>

        {/* Role */}
        <FieldGroup
          heading="Role"
          headingId="staff-add-role"
          description="Pick the smallest role that lets them do the job."
        >
          <div
            role="radiogroup"
            aria-labelledby="staff-add-role"
            aria-describedby={roleError !== undefined ? 'staff-add-role-error' : undefined}
            onKeyDown={handleRoleKeyDown}
            className="flex flex-col"
            style={{ gap: '8px' }}
          >
            {ROLE_OPTIONS.map((opt) => (
              <RoleOption
                key={opt.id}
                role={opt.id}
                desc={opt.desc}
                checked={form.role === opt.id}
                tabIndex={focusedRole === opt.id ? 0 : -1}
                buttonRef={(el) => {
                  roleButtonRefs.current.set(opt.id, el);
                }}
                onSelect={() => patch({ role: opt.id })}
              />
            ))}
          </div>
          {roleError !== undefined ? (
            <p
              id="staff-add-role-error"
              role="alert"
              className="m-0"
              style={{
                fontSize: '13px',
                color: 'var(--fg-danger)',
                marginTop: '8px',
              }}
            >
              {roleError}
            </p>
          ) : null}
        </FieldGroup>

        {/* Initial permissions */}
        <FieldGroup
          heading="Initial permissions"
          headingId="staff-add-permissions"
          description="Permissions are role-derived."
        >
          <PermissionsPreview role={form.role} />
        </FieldGroup>
      </Card>

      {/* Submit-level error (non-blocking; duplicate / forbidden / error) */}
      {submitError !== null ? (
        <p
          role="alert"
          aria-live="polite"
          className="m-0"
          style={{
            marginTop: '12px',
            fontSize: '13px',
            color: 'var(--fg-danger)',
          }}
        >
          {submitError}
        </p>
      ) : null}

      {/* Action row */}
      <div
        data-testid="staff-add-action-row"
        className={
          compact
            ? 'mt-4 flex flex-col items-stretch'
            : 'mt-5 flex items-center'
        }
        style={{ gap: '12px', marginTop: '20px' }}
      >
        <p
          className={compact ? 'm-0' : 'm-0 flex-1'}
          style={{ fontSize: '12px', color: 'var(--fg-tertiary)' }}
        >
          We'll email{' '}
          <span className="font-mono">
            {form.email.trim().length > 0 ? form.email : 'the address above'}
          </span>{' '}
          a one-time link valid for 24 hours.
        </p>
        <Button
          type="button"
          variant="ghost"
          full={compact}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          full={compact}
          onClick={handleSubmit}
          aria-disabled={!canSubmit && submitAttempted}
        >
          <Icon name="mail" size={16} aria-hidden />
          Send invitation
        </Button>
      </div>
    </section>
  );
}

// ── FieldGroup wrapper ─────────────────────────────────────────

function FieldGroup({
  heading,
  headingId,
  description,
  children,
}: {
  readonly heading: string;
  readonly headingId: string;
  readonly description?: string;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <section aria-labelledby={headingId} style={{ marginBottom: '20px' }}>
      <h2
        id={headingId}
        className="m-0 font-bold"
        style={{
          fontSize: '16px',
          color: 'var(--fg-primary)',
          marginBottom: description !== undefined ? '4px' : '10px',
        }}
      >
        {heading}
      </h2>
      {description !== undefined ? (
        <p
          className="m-0"
          style={{
            fontSize: '13px',
            color: 'var(--fg-tertiary)',
            marginBottom: '16px',
          }}
        >
          {description}
        </p>
      ) : null}
      {children}
    </section>
  );
}

// ── RoleOption ─────────────────────────────────────────────────

function RoleOption({
  role,
  desc,
  checked,
  tabIndex,
  buttonRef,
  onSelect,
}: {
  readonly role: PlatformRole;
  readonly desc: string;
  readonly checked: boolean;
  readonly tabIndex: number;
  readonly buttonRef: (el: HTMLButtonElement | null) => void;
  readonly onSelect: () => void;
}): React.ReactElement {
  const descId = `staff-add-role-${role}-desc`;
  return (
    // role-card radiogroup pattern — kit Button has no radiogroup
    // variant. a11y: `aria-label` carries JUST the role label; the
    // description rides on `aria-describedby`. Roving-focus `tabIndex`
    // keeps the group a single tab order entry per WAI-ARIA.
    // eslint-disable-next-line @sparta/no-hand-rolled-button
    <button
      type="button"
      ref={buttonRef}
      role="radio"
      aria-checked={checked}
      aria-label={ROLE_LABEL[role]}
      aria-describedby={descId}
      tabIndex={tabIndex}
      onClick={onSelect}
      className="flex w-full cursor-pointer items-start text-left transition-colors"
      style={{
        padding: '12px 14px',
        borderRadius: 'var(--radius-lg)',
        border: '1.5px solid',
        borderColor: checked ? 'var(--brand-500)' : 'var(--border-default)',
        background: checked
          ? 'color-mix(in oklab, var(--brand-500) 8%, var(--bg-surface))'
          : 'var(--bg-surface)',
        gap: '12px',
      }}
    >
      <span
        aria-hidden="true"
        className="inline-flex shrink-0 items-center justify-center rounded-full"
        style={{
          width: '18px',
          height: '18px',
          border: '2px solid',
          borderColor: checked ? 'var(--brand-500)' : 'var(--border-default)',
          background: checked ? 'var(--brand-500)' : 'transparent',
          marginTop: '2px',
        }}
      >
        {checked ? (
          <span
            className="rounded-full"
            style={{ width: '6px', height: '6px', background: '#fff' }}
          />
        ) : null}
      </span>
      <span className="flex flex-1 flex-col items-start" style={{ gap: '4px' }}>
        <StaffRoleChip role={role} size="sm" />
        <span
          id={descId}
          style={{ fontSize: '13px', color: 'var(--fg-secondary)' }}
        >
          {desc}
        </span>
      </span>
    </button>
  );
}

// ── PermissionsPreview ─────────────────────────────────────────

function PermissionsPreview({
  role,
}: {
  readonly role: PlatformRole | null;
}): React.ReactElement {
  if (role === null) {
    return (
      <p
        className="m-0"
        style={{ fontSize: '13px', color: 'var(--fg-tertiary)' }}
      >
        Pick a role above to preview the permissions it grants.
      </p>
    );
  }
  const slugs = PERMISSIONS_BY_ROLE[role] ?? [];
  return (
    <ul
      data-testid="staff-add-permissions-preview"
      className="m-0 flex list-none flex-wrap p-0"
      style={{ gap: '6px' }}
    >
      {slugs.map((slug) => (
        <PermissionChip key={slug} slug={slug} />
      ))}
    </ul>
  );
}

function PermissionChip({
  slug,
}: {
  readonly slug: AdminPermissionSlug;
}): React.ReactElement {
  return (
    <li
      className="inline-flex items-center font-mono"
      style={{
        padding: '4px 10px',
        borderRadius: 'var(--radius-full)',
        background: 'var(--bg-sunken)',
        color: 'var(--fg-secondary)',
        fontSize: '12px',
        gap: '6px',
      }}
    >
      <span aria-hidden="true" style={{ color: 'var(--success-500)' }}>
        <Icon name="check" size={12} />
      </span>
      {slug}
    </li>
  );
}
